import { type FC, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderOpen,
  ListChecks,
  ShieldCheck,
  Database,
  Bell,
  Inbox,
  ChevronLeft,
  type LucideIcon,
} from 'lucide-react';
import { AxisFinanceLogo } from './AxisFinanceLogo';
import { cn } from '@/lib/utils';
import { usePermission } from '@/auth/use-permission';
import {
  DASHBOARD_VIEW,
  INGESTION_VIEW,
  PARSING_VIEW,
  VALIDATION_VIEW,
  ACCESS_VIEW,
  MASTERS_VIEW,
  NOTIFICATIONS_VIEW,
} from '@loanaudit/shared';

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  permission: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    label: 'Workspace',
    items: [
      { label: 'Dashboard',     to: '/',              icon: LayoutDashboard, permission: DASHBOARD_VIEW },
      { label: 'Loan Files',    to: '/loan-files',    icon: FolderOpen,      permission: INGESTION_VIEW },
      { label: 'Review Queue',  to: '/parsing/review-queue', icon: Inbox,    permission: PARSING_VIEW },
      { label: 'Findings',      to: '/findings',      icon: ListChecks,      permission: VALIDATION_VIEW },
      // Hidden for now — no backing routes/pages yet (lead to the dashboard
      // fallback). Restore when Reports/Analytics are built.
      // { label: 'Reports',       to: '/reports',       icon: FileText,        permission: REPORTING_VIEW },
      // { label: 'Analytics',     to: '/analytics',     icon: BarChart3,       permission: DETECTION_VIEW },
      { label: 'Notifications', to: '/notifications', icon: Bell,            permission: NOTIFICATIONS_VIEW },
    ],
  },
  // "Engine" group hidden for now — its only item (Runs → /runs) has no page yet.
  // {
  //   label: 'Engine',
  //   items: [
  //     { label: 'Runs',        to: '/runs',          icon: Cpu,             permission: INGESTION_VIEW },
  //   ],
  // },
  {
    label: 'Admin',
    items: [
      // Team, Config, System Admin and Settings hidden for now (user request).
      // Restore by uncommenting + re-adding the icon/permission imports.
      // { label: 'Team',            to: '/admin/team',             icon: Users,        permission: ACCESS_VIEW },
      { label: 'Access Manager',  to: '/admin/access-manager',   icon: ShieldCheck,  permission: ACCESS_VIEW },
      { label: 'Masters',         to: '/admin/masters',          icon: Database,     permission: MASTERS_VIEW },
      // { label: 'Config',          to: '/admin/config',           icon: Sliders,      permission: ADMIN_EDIT_CONFIG },
      // { label: 'System Admin',    to: '/admin/system',           icon: Settings,     permission: ADMIN_VIEW },
      // { label: 'Settings',        to: '/settings',               icon: Settings,     permission: SETTINGS_VIEW },
    ],
  },
];

export interface SidebarProps {
  variant?: 'desktop' | 'mobile';
  /** Only used in desktop variant */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** Callback for mobile variant — close the sheet when a link is tapped */
  onNavigate?: () => void;
}

const GroupLabel: FC<{ label: string; collapsed: boolean }> = ({ label, collapsed }) => (
  <div
    className={cn(
      'px-4 pt-4 pb-1.5 text-micro font-semibold tracking-[0.08em] uppercase text-on-surface-variant',
      'transition-opacity duration-200',
      collapsed ? 'opacity-0 h-0 overflow-hidden pointer-events-none' : 'opacity-100',
    )}
  >
    {label}
  </div>
);

const NavRow: FC<{
  item: NavItem;
  collapsed: boolean;
  active: boolean;
  onClick?: () => void;
}> = ({ item, collapsed, active, onClick }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      onClick={onClick}
      className={cn(
        'group flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg transition-colors min-h-11',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-low',
        active
          ? 'bg-secondary-container text-primary font-semibold'
          : 'text-on-surface-variant hover:bg-surface-variant/60',
      )}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? item.label : undefined}
    >
      <Icon className="w-5 h-5 shrink-0" />
      <span
        className={cn(
          'text-label truncate transition-opacity duration-200',
          collapsed ? 'opacity-0 w-0' : 'opacity-100',
        )}
      >
        {item.label}
      </span>
    </Link>
  );
};

export const Sidebar: FC<SidebarProps> = ({
  variant = 'desktop',
  collapsed = false,
  onToggleCollapsed,
  onNavigate,
}) => {
  const location = useLocation();
  const { has, isSuperAdmin } = usePermission();
  const actualCollapsed = variant === 'mobile' ? false : collapsed;

  return (
    <aside
      className={cn(
        'bg-surface-container-low flex flex-col h-full',
        variant === 'desktop' && 'border-r border-outline-variant',
      )}
      style={{
        width: variant === 'desktop' ? (actualCollapsed ? '4.5rem' : '16rem') : '100%',
        transition: 'width 300ms cubic-bezier(0.32, 0.72, 0, 1)',
      }}
    >
      {/* Logo + product title */}
      <div
        className={cn(
          'border-b border-outline-variant shrink-0 overflow-hidden',
          actualCollapsed
            ? 'flex flex-col items-center justify-center h-16 px-2'
            : 'flex flex-col items-start gap-1.5 px-4 py-3.5',
        )}
      >
        <AxisFinanceLogo height={actualCollapsed ? 18 : 22} />
        {!actualCollapsed && (
          <span className="font-display text-title font-bold leading-[1.1] tracking-tight text-primary whitespace-nowrap">
            Agentic Loan Audit
          </span>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto scrollbar-subtle py-2">
        {NAV.map((group) => {
          const visibleItems = group.items.filter((i) => isSuperAdmin || has(i.permission));
          if (!visibleItems.length) return null;
          return (
            <div key={group.label}>
              <GroupLabel label={group.label} collapsed={actualCollapsed} />
              <div className="flex flex-col gap-0.5">
                {visibleItems.map((item) => {
                  const active =
                    item.to === '/'
                      ? location.pathname === '/'
                      : location.pathname.startsWith(item.to);
                  return (
                    <NavRow
                      key={item.to}
                      item={item}
                      collapsed={actualCollapsed}
                      active={active}
                      onClick={onNavigate}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Collapse toggle (desktop only) */}
      {variant === 'desktop' && (
        <div className="p-2 border-t border-outline-variant">
          <button
            onClick={onToggleCollapsed}
            className="w-full h-9 flex items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-variant/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-low"
            aria-label={actualCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronLeft
              className={cn(
                'w-5 h-5 transition-transform duration-300',
                actualCollapsed && 'rotate-180',
              )}
            />
          </button>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;

/**
 * Helper: toggle desktop sidebar from state in parent layout.
 *
 * AppShell is rendered per-route, so it remounts on every tab switch. Persist
 * the collapsed state in localStorage so it survives remounts — the sidebar
 * only changes when the toggle button is clicked, not on navigation.
 */
const SIDEBAR_KEY = 'la.sidebarCollapsed';

export function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
      } catch {
        /* ignore storage failures (private mode, quota) */
      }
      return next;
    });
  }, []);

  return [collapsed, toggle];
}
